import type { SegmentType } from '../types';

export function buildSegmentPath(
  from: { x: number; y: number },
  to: { x: number; y: number },
  fromDir: { dx: number; dy: number },
  toDir: { dx: number; dy: number },
  type: SegmentType,
): string {
  switch (type) {
    case 'bezier': {
      const dist = Math.hypot(to.x - from.x, to.y - from.y);
      const cpOffset = Math.max(50, dist * 0.4);
      const cp1 = { x: from.x + fromDir.dx * cpOffset, y: from.y + fromDir.dy * cpOffset };
      const cp2 = { x: to.x + toDir.dx * cpOffset, y: to.y + toDir.dy * cpOffset };
      return `M ${from.x},${from.y} C ${cp1.x},${cp1.y} ${cp2.x},${cp2.y} ${to.x},${to.y}`;
    }
    case 'straight':
      return `M ${from.x},${from.y} L ${to.x},${to.y}`;

    case 'ortho90': {
      // L-shape with small escape offset (10px) and toDir awareness.
      // Corner sits near the target (vs elbowed which centers it).
      const OFFSET = 10;
      const e1x = from.x + fromDir.dx * OFFSET;
      const e1y = from.y + fromDir.dy * OFFSET;
      const e2x = to.x + toDir.dx * OFFSET;
      const e2y = to.y + toDir.dy * OFFSET;
      const fromH = Math.abs(fromDir.dx) > Math.abs(fromDir.dy);

      if (fromH) {
        // Horizontal exit → corner at (e2x, e1y)
        return `M ${from.x},${from.y} L ${e1x},${e1y} L ${e2x},${e1y} L ${e2x},${e2y} L ${to.x},${to.y}`;
      } else {
        // Vertical exit → corner at (e1x, e2y)
        return `M ${from.x},${from.y} L ${e1x},${e1y} L ${e1x},${e2y} L ${e2x},${e2y} L ${to.x},${to.y}`;
      }
    }
    case 'angle45': {
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const isHorizontalExit = Math.abs(fromDir.dx) > Math.abs(fromDir.dy);
      const diag = Math.min(absDx, absDy);

      // "Axis-aligned first, then diagonal" when exit direction matches the longer axis
      const axisFirst = (absDx >= absDy) ? isHorizontalExit : !isHorizontalExit;
      if (axisFirst) {
        // Straight along the longer axis, then diagonal to target
        const midX = isHorizontalExit
          ? from.x + Math.sign(dx) * (absDx - diag)
          : from.x;
        const midY = isHorizontalExit
          ? from.y
          : from.y + Math.sign(dy) * (absDy - diag);
        return `M ${from.x},${from.y} L ${midX},${midY} L ${to.x},${to.y}`;
      } else {
        // Diagonal first, then straight to target
        const midX = from.x + Math.sign(dx) * diag;
        const midY = from.y + Math.sign(dy) * diag;
        return `M ${from.x},${from.y} L ${midX},${midY} L ${to.x},${to.y}`;
      }
    }

    case 'elbowed': {
      const OFFSET = 20;
      const e1 = { x: from.x + fromDir.dx * OFFSET, y: from.y + fromDir.dy * OFFSET };
      const e2 = { x: to.x + toDir.dx * OFFSET, y: to.y + toDir.dy * OFFSET };
      const fromH = Math.abs(fromDir.dx) > Math.abs(fromDir.dy);
      const toH = Math.abs(toDir.dx) > Math.abs(toDir.dy);

      if (fromH && toH) {
        // H→H: Z-shape with 5 segments
        const midX = (e1.x + e2.x) / 2;
        return `M ${from.x},${from.y} L ${e1.x},${e1.y} L ${midX},${e1.y} L ${midX},${e2.y} L ${e2.x},${e2.y} L ${to.x},${to.y}`;
      } else if (!fromH && !toH) {
        // V→V: Z-shape rotated
        const midY = (e1.y + e2.y) / 2;
        return `M ${from.x},${from.y} L ${e1.x},${e1.y} L ${e1.x},${midY} L ${e2.x},${midY} L ${e2.x},${e2.y} L ${to.x},${to.y}`;
      } else if (fromH && !toH) {
        // H→V: corner
        return `M ${from.x},${from.y} L ${e1.x},${e1.y} L ${e2.x},${e1.y} L ${e2.x},${e2.y} L ${to.x},${to.y}`;
      } else {
        // V→H: corner
        return `M ${from.x},${from.y} L ${e1.x},${e1.y} L ${e1.x},${e2.y} L ${e2.x},${e2.y} L ${to.x},${to.y}`;
      }
    }
  }
}
